import { useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { SettingsDetailPage, type DetailConfig, type DetailDataAdapter }
  from '../../modules/SettingsDetailPage';
import '../../backlog/backlog.css';
import {
  ACT_CONFIGS, ACT_OPTIONS, MATRIX_TESTS, PROCESS_OPTIONS,
  buildLogicPreview, validateConfig, nextActIdPreview,
  type ActConfig, type ActRule,
} from './actData';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Workflow Configuration → ACT (ALPACA Custom Trigger)
//
// Ported from the approved prototype (ALPACA_CAT_Standardized_ID_Prototype.html,
// 2026-08-13), rendered with THIS product's shared components (FormTitle /
// AppToolbar / AppDataGrid / useNotify / useConfirm) so it sits alongside every
// other form (CLAUDE.md standing rules 7 and 9).
//
// 2026-08-14 — SIMPLIFIED, six tabs → three (user: "too many settings").
// The prototype exposed ~40 controls, and several of them edited the SAME value
// from more than one place, which is worse than merely being long: two controls
// for one setting means the screen can show you a value you did not choose.
// What was removed and why:
//
//   • The condition field had THREE editors (Trigger Setup, Condition Builder,
//     Prep Flow Setup). Now one, on Method Setup, which writes both the model's
//     `conditionField` and `triggerField` so they cannot drift apart.
//   • "Trigger Object" and the WHEN box's object were two fields for one idea —
//     merged into "Applies to".
//   • "Re-evaluate On" (dropdown) and "Re-evaluate if condition changes"
//     (toggle) were the same decision twice — the toggle survives and sets both.
//   • Prep Flow Setup carried "Selected Prep Flow" AND "Matched Flow", two
//     controls on ONE tab bound to the same value. Now a single "Then use".
//   • The whole Supporting Actions tab (8 controls) restated the THEN of the
//     Condition Builder — action / target / value never reached a rule, the
//     preview, or a validation check. Removed; the model keeps its defaults.
//   • Trigger Setup's Priority edited rules[0].priority, which the rule editor
//     below already owns. Removed there.
//
// Genuine settings that are simply rare (execution timing, run-once, stop-
// further-rules, manual override, flow type, condition source) were NOT deleted
// — they moved behind a collapsed "Advanced" disclosure, so they are available
// without being in the way.
//
// 2026-08-14 — EDIT/UPDATE brought onto the app standard (user: "How to Edit
// and Update?"). The prototype edited a rule in an inline panel under the grid
// and had no Delete at all, which is not how any other form in this product
// behaves. Now, per standing rules 7 / 10 / 12:
//
//   • <AppToolbar> in canonical order — form-specific (Copy From Method · Test
//     Rules · Validate · Save Method Setup) · New · Edit · Delete · Refresh ·
//     Export to. Audit is NOT a toolbar action (BO UI Standard v2): it is the
//     config-gated chip on <FormTitle auditFormName>.
//   • New and Edit REPLACE the list with the shared <SettingsDetailPage> — the
//     same full-page detail every settings form uses, so ACT rules get the
//     header + breadcrumb + prev/next record arrows, the top AND bottom action
//     bars, view-then-Edit, required-empty yellow, and the Save trio (Save ·
//     Save and Close · Save and New · Cancel) through the shared useSaveFlow
//     for free. Double-clicking a row opens it too.
//   • Delete goes through the shared useDeleteFlow (danger confirm → reason
//     gate when the form requires one → "Deleted.").
//
// The detail is driven by an in-memory DetailDataAdapter instead of REST,
// because there is no ACT endpoint yet — that is the ONLY thing that changes
// when the API lands (swap the adapter for `basePath`).
//
// ⚠️ NOT PERSISTED. Every edit lives in component state and is lost on reload —
// including a rule saved from the detail view, which reports success because it
// did update the list. The page banner is what covers the reload case. See
// actData.ts for what has to change when it is wired up.
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'rules' | 'method' | 'checks';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'rules',  label: 'Rules',        icon: 'ti-list-check' },
  { id: 'method', label: 'Method Setup', icon: 'ti-git-branch' },
  { id: 'checks', label: 'Checks',       icon: 'ti-shield-check' },
];

/** Label + control on one row. The whole form is built from this so spacing and
 *  label width cannot drift between tabs. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)', paddingTop: 6 }}>
        {label}
      </label>
      <div>{children}</div>
    </>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 150px 1fr',
                  gap: '10px 14px', alignItems: 'start', maxWidth: 1180 }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', maxWidth: 340, padding: '5px 8px', fontSize: 12.5,
  border: '1px solid var(--ai-border, #d7dbe3)', borderRadius: 4,
  color: 'var(--ai-ink)', background: '#fff',
};

function Select({ value, options, onChange, width }:
  { value: string; options: readonly string[]; onChange: (v: string) => void; width?: number }) {
  // A stored value that is not in the list still has to be selectable, or
  // opening the form would silently rewrite it to the first option.
  const opts = options.includes(value) ? options : [value, ...options];
  return (
    <select style={{ ...inputStyle, ...(width ? { width, maxWidth: width } : null) }}
      value={value} onChange={e => onChange(e.target.value)}>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Text({ value, onChange, width }:
  { value: string; onChange: (v: string) => void; width?: number }) {
  return <input style={{ ...inputStyle, ...(width ? { width, maxWidth: width } : null) }}
    value={value} onChange={e => onChange(e.target.value)} />;
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!on)}
      aria-pressed={on}
      style={{ width: 38, height: 20, borderRadius: 10, cursor: 'pointer', padding: 0,
               border: '1px solid var(--ai-border, #d7dbe3)',
               background: on ? 'var(--ai-green, #16a34a)' : '#d7dbe3', position: 'relative' }}>
      <span style={{ position: 'absolute', top: 1, left: on ? 19 : 1, width: 16, height: 16,
                     borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
    </button>
  );
}

/** The rule's action string as stored — "Select Prep Flow: {flow}". Keeping the
 *  prefix in one place means the rule editor can offer the method's prep flows
 *  as a list instead of a free-text box nobody can spell consistently. */
const FLOW_PREFIX = 'Select Prep Flow: ';
const flowOf   = (action: string) => action.startsWith(FLOW_PREFIX) ? action.slice(FLOW_PREFIX.length) : action;
const actionOf = (flow: string)   => `${FLOW_PREFIX}${flow}`;

/** Preview of the Rule ID a new rule WOULD get. Preview only — like the ACT id,
 *  the real one must come from ISequentialIdGenerator when this is persisted, so
 *  two people adding a rule at once cannot both be handed R03 (§14a Rule B). */
function nextRuleIdPreview(rules: readonly ActRule[]): string {
  const highest = rules.reduce((max, r) => {
    const n = parseInt(r.ruleId.replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `R${String(highest + 1).padStart(2, '0')}`;
}

export default function ActConfigPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  const runDelete = useDeleteFlow();

  const [configs, setConfigs] = useState<ActConfig[]>(ACT_CONFIGS);
  const [activeKey, setActiveKey] = useState(ACT_CONFIGS[0]!.key);
  const [tab, setTab] = useState<TabId>('rules');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  /** null = the list. Otherwise the detail is open: { ruleId: null } is New. */
  const [detail, setDetail] = useState<{ ruleId: string | null } | null>(null);

  const cfg = configs.find(c => c.key === activeKey)!;
  const flowNames = useMemo(() => cfg.prepFlows.map(f => f.name), [cfg.prepFlows]);

  /** Apply a change to the ACTIVE configuration only. */
  const patch = (changes: Partial<ActConfig>) =>
    setConfigs(prev => prev.map(c => (c.key === activeKey ? { ...c, ...changes } : c)));

  const checks = useMemo(() => validateConfig(cfg), [cfg]);
  const failing = checks.filter(c => !c.pass);

  const selectedRuleIds = cfg.rules.filter(r => selectedIds.has(r.ruleId)).map(r => r.ruleId);
  const singleSelectedId = selectedRuleIds.length === 1 ? selectedRuleIds[0]! : null;

  // ── the shared full-page detail view ──────────────────────────────────────
  //
  // <SettingsDetailPage> reloads the record whenever `config` or `adapter`
  // changes identity, so BOTH have to be referentially stable — rebuilding
  // either inline would wipe the fields the user is typing into on every
  // keystroke. The adapter reads live state through a ref for that reason.

  const live = useRef({ configs, activeKey });
  useEffect(() => { live.current = { configs, activeKey }; });

  const detailConfig: DetailConfig = useMemo(() => ({
    formName: 'ACT',
    entityLabel: 'ACT Rule',
    idField: 'ruleId',
    fields: [
      // Rule Name first ON PURPOSE: <SettingsDetailPage> titles the record from
      // the FIRST field, and "ACT Rule – Select Filtration Prep" identifies it
      // in the header and breadcrumb far better than "ACT Rule – R01".
      { key: 'name', label: 'Rule Name', type: 'text', required: true, maxLength: 80 },
      { key: 'ruleId', label: 'Rule ID', type: 'readonly' },
      { key: 'priority', label: 'Priority', type: 'text', required: true,
        hint: 'Lower runs first. Two rules must not share a priority — the winner would be arbitrary.' },
      { key: 'condition', label: 'Condition', type: 'text', required: true,
        hint: 'e.g. NTU > 1.0' },
      // A static list of THIS method's prep flows: no endpoint could serve it
      // independently, and free text here is what fails the "Action target
      // valid" check.
      { key: 'flow', label: 'Then use', type: 'select', required: true,
        options: flowNames.map(n => ({ id: n, name: n })), noneLabel: '— Select a prep flow —' },
      { key: 'status', label: 'Status', type: 'select', required: true,
        options: ACT_OPTIONS.status.map(s => ({ id: s, name: s })), noneLabel: '— Select —' },
    ],
  }), [flowNames]);

  const detailAdapter: DetailDataAdapter = useMemo(() => ({
    load: async (id) => {
      const { configs: cs, activeKey: k } = live.current;
      const r = cs.find(c => c.key === k)?.rules.find(x => x.ruleId === id);
      if (!r) throw new Error('Rule not found');
      return {
        ruleId: r.ruleId, name: r.name, priority: String(r.priority),
        condition: r.condition, flow: flowOf(r.action), status: r.status,
      };
    },
    create: async (payload) => {
      const { configs: cs, activeKey: k } = live.current;
      const owner = cs.find(c => c.key === k)!;
      const ruleId = nextRuleIdPreview(owner.rules);
      const created: ActRule = {
        ruleId,
        name: String(payload.name ?? 'New rule'),
        priority: Number(payload.priority) || (owner.rules.at(-1)?.priority ?? 0) + 10,
        // Derived, not asked for: the trigger restates the method's watched
        // field, which is why it is not a field on this form.
        trigger: `On Prep Evaluation + ${owner.conditionField} Changed`,
        condition: String(payload.condition ?? ''),
        action: actionOf(String(payload.flow ?? owner.matchedFlow)),
        target: owner.thenTarget,
        status: (payload.status === 'Disabled' ? 'Disabled' : 'Active'),
      };
      setConfigs(prev => prev.map(c => (c.key === k ? { ...c, rules: [...c.rules, created] } : c)));
      return ruleId;
    },
    update: async (id, payload) => {
      const { activeKey: k } = live.current;
      setConfigs(prev => prev.map(c => (c.key !== k ? c : {
        ...c,
        rules: c.rules.map(r => (r.ruleId !== id ? r : {
          ...r,
          name: String(payload.name ?? r.name),
          priority: Number(payload.priority) || r.priority,
          condition: String(payload.condition ?? r.condition),
          action: actionOf(String(payload.flow ?? flowOf(r.action))),
          status: (payload.status === 'Disabled' ? 'Disabled' : 'Active'),
        })),
      })));
    },
  }), []);

  // ── toolbar actions ───────────────────────────────────────────────────────

  const handleSave = async () => {
    const ok = await confirm.confirm({ title: 'Save ACT configuration?', message: 'Do you want to Save?' });
    if (!ok) return;
    // Honest about the state of things: claiming "Saved successfully." when
    // nothing is stored would be the single most misleading thing this screen
    // could do — the next person to open it would think they had lost work.
    notify.info(`${cfg.catId} is not persisted yet — this screen is a layout preview. `
              + `${cfg.rules.length} rule(s) would be saved.`);
  };

  const handleTestRules = () => {
    const first = cfg.rules[0];
    notify.info(first
      ? `First matching rule for ${cfg.conditionField} ${cfg.operator} ${cfg.value}: `
        + `${first.ruleId} — ${first.action}`
      : 'No rules to test.');
  };

  const duplicateRule = () => {
    const source = cfg.rules.find(r => r.ruleId === singleSelectedId);
    if (!source) return;
    const copy: ActRule = {
      ...source,
      ruleId: nextRuleIdPreview(cfg.rules),
      name: `${source.name} (copy)`,
      priority: (cfg.rules.at(-1)?.priority ?? 0) + 10,
    };
    patch({ rules: [...cfg.rules, copy] });
    setSelectedIds(new Set([copy.ruleId]));
    notify.success(`${copy.ruleId} created from ${source.ruleId}.`);
  };

  const handleDelete = async () => {
    const ids = selectedRuleIds;
    if (ids.length === 0) return;
    await runDelete({
      formName: 'ACT',
      message: `Delete ${ids.length} ACT rule${ids.length === 1 ? '' : 's'}?`,
      delete: () => patch({ rules: cfg.rules.filter(r => !ids.includes(r.ruleId)) }),
      onDeleted: () => setSelectedIds(new Set()),
    });
  };

  /** Reload from the sample data — the same contract as any other form's
   *  Refresh: it discards unsaved edits because it re-reads the source. */
  const handleRefresh = () => {
    setConfigs(ACT_CONFIGS);
    setSelectedIds(new Set());
    notify.success('Refreshed.');
  };

  const openDetail = (ruleId: string | null) => setDetail({ ruleId });

  // ── ACT Rules grid ────────────────────────────────────────────────────────
  //
  // Trigger and Target were dropped as COLUMNS, not as data: every sample row's
  // Target is "Sample Test" and its Trigger restates the method's When + watched
  // field, so both were a constant repeated on every row — and together they
  // pushed the grid into horizontal scroll. Both live on Method Setup now.

  const ruleCols: AppColumn<ActRule>[] = [
    { field: 'priority', header: 'Priority', width: '90px', text: r => String(r.priority) },
    { field: 'ruleId',   header: 'Rule ID',  width: '90px' },
    { field: 'name',     header: 'Rule Name', width: '220px' },
    { field: 'condition', header: 'Condition', width: '220px' },
    { field: 'action',   header: 'Prep Flow', width: '300px', text: r => flowOf(r.action) },
    { field: 'status',   header: 'Status',   width: '110px', type: 'status' },
  ];

  // New / Edit REPLACE the list with the shared full-page detail (rule 12) —
  // never a popup, and never rendered alongside the list.
  if (detail) {
    return (
      <SettingsDetailPage
        config={detailConfig}
        adapter={detailAdapter}
        id={detail.ruleId}
        title="ACT"
        group="Workflow Configuration"
        siblingIds={cfg.rules.map(r => r.ruleId)}
        onHome={() => setDetail(null)}
        onNavigate={id => setDetail({ ruleId: id })}
        onClose={() => setDetail(null)}
        // The list reads the same component state the adapter just wrote, so
        // there is nothing to re-fetch; keep the saved row selected.
        onSaved={() => detail.ruleId && setSelectedIds(new Set([detail.ruleId]))}
      />
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Workflow Configuration</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">ACT</span>
          </div>
          <div className="dp-title-row">
            {/* Audit Trail is a VIEW-ONLY chip on the title row, not a toolbar
                action (BO UI Standard v2) — and it hides itself when Settings →
                Audit Trail Configuration has audit off for this form. */}
            <FormTitle pageId="act-prepflow" title="ACT – ALPACA Custom Trigger" auditFormName="ACT" />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Layout preview with sample data — nothing on this screen is saved yet.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <button className="ca-btn" onClick={() => setShowCopy(true)}>
                  <i className="ti ti-copy" style={{ marginRight: 4 }} />Copy From Method
                </button>
                <button className="ca-btn" onClick={handleTestRules}>
                  <i className="ti ti-player-play" style={{ marginRight: 4 }} />Test Rules
                </button>
                <button className="ca-btn" onClick={() => setTab('checks')}>
                  <i className="ti ti-shield-check" style={{ marginRight: 4 }} />Validate
                  {failing.length > 0 && (
                    <span style={{ marginLeft: 5, color: 'var(--ai-amber, #b45309)', fontWeight: 700 }}>
                      {failing.length}
                    </span>
                  )}
                </button>
                <button className="ca-btn" onClick={() => void handleSave()}>
                  <i className="ti ti-device-floppy" style={{ marginRight: 4 }} />Save
                </button>
                <button className="ca-btn" onClick={duplicateRule} disabled={!singleSelectedId}>
                  <i className="ti ti-copy" style={{ marginRight: 4 }} />Duplicate
                </button>
              </>
            }
            onNew={() => openDetail(null)}
            newLabel="New Rule"
            onEdit={() => singleSelectedId && openDetail(singleSelectedId)}
            editDisabled={!singleSelectedId}
            onDelete={() => void handleDelete()}
            deleteDisabled={selectedRuleIds.length === 0}
            onRefresh={handleRefresh}
            exportData={{
              filename: `${cfg.catId}-rules`,
              ...gridExportMatrix(cfg.rules, ruleCols),
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', minHeight: 0, flex: 1 }}>

          {/* ── left: the ACT configurations ─────────────────────────────── */}
          <aside className="bl-card" style={{ width: 300, flexShrink: 0, display: 'flex',
                                              flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 12px', borderBottom: '1px solid var(--ai-border, #e5e7eb)' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ai-ink)' }}>Methods</span>
              <button className="ca-btn" onClick={() => setShowAddMethod(true)}>
                <i className="ti ti-plus" style={{ marginRight: 3 }} />Method
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {configs.map(c => {
                const on = c.key === activeKey;
                return (
                  <button key={c.key} onClick={() => { setActiveKey(c.key); setSelectedIds(new Set()); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                             padding: '9px 12px', border: 'none',
                             borderBottom: '1px solid var(--ai-border, #eef0f4)',
                             borderLeft: on ? '3px solid var(--ai-loader-color, #e8670a)' : '3px solid transparent',
                             background: on ? 'var(--ai-row-hover, #f1f3f7)' : 'transparent' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>
                      {c.catId} · {c.test}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 2 }}>
                      {c.matrix} · {c.process}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 3 }}>
                      <span style={{ background: 'var(--ai-pill-ok-bg, #e7f6ec)', color: 'var(--ai-pill-ok-fg, #16794a)',
                                     borderRadius: 3, padding: '1px 5px', marginRight: 5 }}>
                        {c.status}
                      </span>
                      {c.summary}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ── right: the configuration itself ──────────────────────────── */}
          <section className="bl-card bl-card-flex" style={{ flex: 1, minWidth: 0 }}>

            {/* Matrix and Process were dropped from the summary strip — the left
                panel already shows both under every method name. */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '12px 14px',
                          borderBottom: '1px solid var(--ai-border, #e5e7eb)' }}>
              {([
                ['ACT ID', cfg.catId], ['Test', cfg.test], ['Method', cfg.method],
                ['Rules', `${cfg.rules.length} Rules`],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k} style={{ border: '1px solid var(--ai-border, #e5e7eb)', borderRadius: 4,
                                      padding: '6px 12px', minWidth: 120, background: '#fafbfc' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--ai-muted)', textTransform: 'uppercase',
                                letterSpacing: .4 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)', marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>

            <div className="sa-filter-row" style={{ padding: '8px 12px 0' }}>
              {TABS.map(t => (
                <button key={t.id} className={`sa-filter-btn${tab === t.id ? ' active' : ''}`}
                  onClick={() => setTab(t.id)}>
                  <i className={`ti ${t.icon}`} style={{ fontSize: 13, marginRight: 4 }} />{t.label}
                  {t.id === 'checks' && failing.length > 0 && (
                    <span style={{ marginLeft: 5, color: 'var(--ai-amber, #b45309)', fontWeight: 700 }}>
                      {failing.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 14px 22px' }}>

              {/* ── Rules ─────────────────────────────────────────────────── */}
              {tab === 'rules' && (
                <>
                  <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', marginBottom: 8 }}>
                    Tick a rule and use <b>Edit</b>, or double-click the row.
                  </div>
                  <AppDataGrid<ActRule>
                    rows={cfg.rules}
                    columns={ruleCols}
                    getRowId={r => r.ruleId}
                    defaultPageSize={20}
                    selectable
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    onRowDoubleClick={r => openDetail(r.ruleId)}
                    emptyText="No ACT rules defined for this method yet."
                  />
                </>
              )}

              {/* ── Method Setup ──────────────────────────────────────────── */}
              {tab === 'method' && (
                <>
                  <Grid2>
                    <Field label="Applies to">
                      {/* One control for what used to be "Trigger Object" AND the
                          Condition Builder's WHEN object. */}
                      <Select value={cfg.triggerObject} options={ACT_OPTIONS.triggerObject}
                        onChange={v => patch({ triggerObject: v, whenObject: v })} />
                    </Field>
                    <Field label="When">
                      <Select value={cfg.whenEvent} options={ACT_OPTIONS.whenEvent}
                        onChange={v => patch({ whenEvent: v })} />
                    </Field>
                  </Grid2>

                  <div style={{ marginTop: 14, border: '1px solid var(--ai-border, #e5e7eb)',
                                borderRadius: 4, padding: 12, background: '#fafbfc', maxWidth: 1180 }}>
                    <div style={{ fontSize: 12, letterSpacing: .6, color: 'var(--ai-muted)', marginBottom: 8 }}>
                      IF
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* The ONE condition-field editor. It writes triggerField too,
                          so the watched field and the compared field cannot drift —
                          they were separate controls in three tabs before. */}
                      <Select value={cfg.conditionField} options={ACT_OPTIONS.conditionField} width={190}
                        onChange={v => patch({ conditionField: v, triggerField: v })} />
                      <Select value={cfg.operator} options={ACT_OPTIONS.operator} width={80}
                        onChange={v => patch({ operator: v })} />
                      <Text value={cfg.value} onChange={v => patch({ value: v })} width={190} />
                      {!cfg.secondary && (
                        <button className="ca-btn"
                          onClick={() => patch({ secondary: { field: 'Oxidizer', operator: '=', value: 'Present', flow: cfg.matchedFlow } })}>
                          + Add AND
                        </button>
                      )}
                    </div>

                    {cfg.secondary && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 4 }}>AND</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Select value={cfg.secondary.field} options={ACT_OPTIONS.conditionField} width={190}
                            onChange={v => patch({ secondary: { ...cfg.secondary!, field: v } })} />
                          <Select value={cfg.secondary.operator} options={ACT_OPTIONS.operator} width={80}
                            onChange={v => patch({ secondary: { ...cfg.secondary!, operator: v } })} />
                          <Text value={cfg.secondary.value} width={190}
                            onChange={v => patch({ secondary: { ...cfg.secondary!, value: v } })} />
                          <button className="ca-btn" onClick={() => patch({ secondary: undefined })}>
                            Remove AND
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <Grid2>
                      {/* "Selected Prep Flow" and "Matched Flow" were two controls on
                          one tab bound to this same value — now one. */}
                      <Field label="Then use">
                        <Select value={cfg.matchedFlow} options={flowNames} onChange={v => patch({ matchedFlow: v })} />
                      </Field>
                      <Field label="Otherwise use">
                        <Select value={cfg.elseFlow} options={flowNames} onChange={v => patch({ elseFlow: v })} />
                      </Field>
                      <Field label="Re-evaluate on change">
                        {/* Was ALSO a "Re-evaluate On" dropdown on Trigger Setup —
                            one decision, so one control, writing both fields. */}
                        <Toggle on={cfg.reevaluateOnChange}
                          onChange={v => patch({
                            reevaluateOnChange: v,
                            reevaluateOn: v ? 'Condition Field Changed' : 'Never',
                          })} />
                      </Field>
                    </Grid2>
                  </div>

                  {/* The plain-English restatement — derived live, so it is a real
                      check on the fields above rather than decoration. */}
                  <pre data-testid="logic-preview"
                    style={{ marginTop: 16, padding: 12, fontSize: 12.5, lineHeight: 1.7,
                             fontFamily: 'ui-monospace, Consolas, monospace', whiteSpace: 'pre-wrap',
                             border: '1px solid var(--ai-border, #e5e7eb)', borderRadius: 4,
                             background: '#f7f9fc', color: 'var(--ai-ink)', maxWidth: 1180 }}>
                    {buildLogicPreview(cfg)}
                  </pre>

                  <div style={{ marginTop: 18, border: '1px solid var(--ai-border, #e5e7eb)',
                                borderRadius: 4, padding: 12, background: '#fafcff', maxWidth: 1180 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ai-ink)', marginBottom: 8 }}>
                      Available Prep Flows for this Test / Method
                    </div>
                    <table className="bl-tbl" style={{ width: '100%' }}>
                      <thead><tr><th>Prep Flow</th><th>Purpose</th><th>Default?</th></tr></thead>
                      <tbody>
                        {cfg.prepFlows.map(f => (
                          <tr key={f.name}>
                            <td>{f.name}</td>
                            <td>{f.purpose}</td>
                            <td>{f.isDefault ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Rarely changed, so out of the way — but still reachable. These
                      are the survivors of Trigger Setup and Supporting Actions. */}
                  <div style={{ marginTop: 18, maxWidth: 1180 }}>
                    <button className="ca-btn" onClick={() => setShowAdvanced(v => !v)}>
                      <i className={`ti ti-chevron-${showAdvanced ? 'down' : 'right'}`} style={{ marginRight: 4 }} />
                      Advanced
                    </button>
                    {showAdvanced && (
                      <div style={{ marginTop: 12 }}>
                        <Grid2>
                          <Field label="Execution timing">
                            <Select value={cfg.executionTiming} options={ACT_OPTIONS.executionTiming}
                              onChange={v => patch({ executionTiming: v })} />
                          </Field>
                          <Field label="Run once">
                            <Select value={cfg.runOnce} options={ACT_OPTIONS.runOnce}
                              onChange={v => patch({ runOnce: v })} />
                          </Field>
                          <Field label="Stop further rules">
                            <Select value={cfg.stopFurtherRules} options={ACT_OPTIONS.yesNo}
                              onChange={v => patch({ stopFurtherRules: v })} />
                          </Field>
                          <Field label="Allow manual override">
                            <Toggle on={cfg.allowManualOverride} onChange={v => patch({ allowManualOverride: v })} />
                          </Field>
                          <Field label="Flow type">
                            <Select value={cfg.flowType} options={ACT_OPTIONS.flowType}
                              onChange={v => patch({ flowType: v })} />
                          </Field>
                          <Field label="Condition source">
                            <Select value={cfg.conditionSource} options={ACT_OPTIONS.conditionSource}
                              onChange={v => patch({ conditionSource: v })} />
                          </Field>
                        </Grid2>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Checks ────────────────────────────────────────────────── */}
              {tab === 'checks' && (
                <>
                  {failing.length > 0 && (
                    <div className="bl-err" style={{ marginBottom: 12 }}>
                      <i className="ti ti-alert-triangle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                      {failing.length} check{failing.length === 1 ? '' : 's'} did not pass.
                    </div>
                  )}
                  <table className="bl-tbl" style={{ width: '100%' }}>
                    <thead><tr><th>Check</th><th>Result</th><th>Message</th></tr></thead>
                    <tbody>
                      {checks.map(c => (
                        <tr key={c.check}>
                          <td>{c.check}</td>
                          <td style={{ color: c.pass ? 'var(--ai-green, #16a34a)' : 'var(--ai-amber, #b45309)',
                                       fontWeight: 600 }}>
                            {c.pass ? 'Pass' : 'Fail'}
                          </td>
                          <td>{c.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      {showAddMethod && (
        <AddMethodModal
          nextId={nextActIdPreview(configs)}
          onCancel={() => setShowAddMethod(false)}
          onCreate={(matrix, test, method, process) => {
            const created: ActConfig = {
              ...cfg,
              key: `new-${configs.length + 1}`,
              catId: nextActIdPreview(configs),
              matrix, test, method, process,
              summary: 'new configuration',
              rules: [],
            };
            setConfigs(prev => [...prev, created]);
            setActiveKey(created.key);
            setSelectedIds(new Set());
            setShowAddMethod(false);
            setTab('rules');
            notify.success(`${created.catId} added for ${test} / ${method}.`);
          }}
        />
      )}

      {showCopy && (
        <CopyFromMethodModal
          configs={configs.filter(c => c.key !== activeKey)}
          onCancel={() => setShowCopy(false)}
          onCopy={sourceKey => {
            const src = configs.find(c => c.key === sourceKey);
            if (!src) return;
            // Rules are copied, IDENTITY is not — copying ACT-002's id onto
            // ACT-005 would give two configurations the same key.
            patch({ rules: src.rules.map(r => ({ ...r })) });
            setSelectedIds(new Set());
            setShowCopy(false);
            notify.success(`Copied ${src.rules.length} rule(s) from ${src.catId}.`);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────────────────────────

function modalShell(children: React.ReactNode, onCancel: () => void) {
  return (
    <div onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
        style={{ background: '#fff', borderRadius: 6, minWidth: 460, maxWidth: 560,
                 boxShadow: '0 10px 40px rgba(15,23,42,.25)' }}>
        {children}
      </div>
    </div>
  );
}

/** Matrix → Test → Method, in that order. Each list stays disabled until the one
 *  before it is chosen, because a Test only means something within a Matrix and a
 *  Method only within a Test. */
function AddMethodModal({ nextId, onCancel, onCreate }: {
  nextId: string;
  onCancel: () => void;
  onCreate: (matrix: string, test: string, method: string, process: string) => void;
}) {
  const [matrix, setMatrix] = useState('');
  const [test, setTest] = useState('');
  const [method, setMethod] = useState('');
  const [process, setProcess] = useState(PROCESS_OPTIONS[0]!);

  const tests = matrix ? (MATRIX_TESTS[matrix] ?? []) : [];
  const methods = tests.find(t => t.test === test)?.methods ?? [];

  return modalShell(
    <>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ai-border, #e5e7eb)',
                    fontSize: 13, fontWeight: 700, color: 'var(--ai-ink)' }}>
        Add ACT Method Configuration — {nextId}
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '10px 12px', alignItems: 'center' }}>
          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Matrix</label>
          <select style={inputStyle} value={matrix}
            onChange={e => { setMatrix(e.target.value); setTest(''); setMethod(''); }}>
            <option value="">Select Matrix…</option>
            {Object.keys(MATRIX_TESTS).map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Test</label>
          <select style={inputStyle} value={test} disabled={!matrix}
            onChange={e => {
              setTest(e.target.value);
              // A Test with exactly one Method needs no second decision.
              const only = tests.find(t => t.test === e.target.value)?.methods ?? [];
              setMethod(only.length === 1 ? only[0]! : '');
            }}>
            <option value="">{matrix ? 'Select Test…' : 'Select Matrix first…'}</option>
            {tests.map(t => <option key={t.test} value={t.test}>{t.test}</option>)}
          </select>

          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Method</label>
          <select style={inputStyle} value={method} disabled={!test}
            onChange={e => setMethod(e.target.value)}>
            <option value="">{test ? 'Select Method…' : 'Select Test first…'}</option>
            {methods.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Process</label>
          <select style={inputStyle} value={process} onChange={e => setProcess(e.target.value)}>
            {PROCESS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--ai-muted)', lineHeight: 1.6 }}>
          Select <b>Matrix</b> first. The Test list is filtered by Matrix, and the Method list by Test.
          A Method is required before the ACT configuration can be added.
        </div>
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ai-border, #e5e7eb)',
                    display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn" disabled={!method}
          onClick={() => onCreate(matrix, test, method, process)}>Add Method</button>
      </div>
    </>,
    onCancel,
  );
}

function CopyFromMethodModal({ configs, onCancel, onCopy }: {
  configs: ActConfig[]; onCancel: () => void; onCopy: (key: string) => void;
}) {
  const [source, setSource] = useState(configs[0]?.key ?? '');
  return modalShell(
    <>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ai-border, #e5e7eb)',
                    fontSize: 13, fontWeight: 700, color: 'var(--ai-ink)' }}>
        Copy ACT Configuration From Method
      </div>
      <div style={{ padding: 16 }}>
        <label style={{ fontSize: 12.5, fontWeight: 600, display: 'block', marginBottom: 6 }}>
          Source Method
        </label>
        <select style={{ ...inputStyle, maxWidth: '100%' }} value={source}
          onChange={e => setSource(e.target.value)}>
          {configs.map(c => (
            <option key={c.key} value={c.key}>
              {c.catId} · {c.test} / {c.method} ({c.rules.length} rules)
            </option>
          ))}
        </select>
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ai-muted)' }}>
          Rules are copied. The ACT ID, Test, Method and Matrix of this configuration are kept.
        </div>
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ai-border, #e5e7eb)',
                    display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn" disabled={!source} onClick={() => onCopy(source)}>Copy Rules</button>
      </div>
    </>,
    onCancel,
  );
}
